import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { BlockedEmail } from '../../types';
import { ToastContainer } from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorBanner } from '../../components/ErrorBanner';
import { useErrorBanner } from '../../hooks/useErrorBanner';
import { TrashIcon } from '@heroicons/react/24/outline';

export const BlocklistManagement: React.FC = () => {
  const [blockedEmails, setBlockedEmails] = useState<BlockedEmail[]>([]);
  const [loading, setLoading] = useState(true);

  // Add form state
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<BlockedEmail | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { toasts, removeToast, showSuccess, showError } = useToast();
  const { error, showError: showBannerError, clearError } = useErrorBanner();

  const fetchBlockedEmails = useCallback(async () => {
    try {
      const data = await api.getBlockedEmails();
      setBlockedEmails(data);
    } catch (err) {
      showBannerError(err instanceof Error ? err.message : 'Failed to load blocked emails');
    } finally {
      setLoading(false);
    }
  }, [showBannerError]);

  useEffect(() => {
    fetchBlockedEmails();
  }, [fetchBlockedEmails]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setAdding(true);
    try {
      await api.addBlockedEmail(email.trim(), reason.trim() || 'Manually blocked');
      setEmail('');
      setReason('');
      showSuccess('Email Blocked', `${email.trim()} has been added to the blocklist`);
      fetchBlockedEmails();
    } catch (err) {
      showError('Failed', err instanceof Error ? err.message : 'Failed to block email');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.unblockEmail(deleteTarget.id);
      showSuccess('Email Unblocked', `${deleteTarget.email} has been removed from the blocklist`);
      setDeleteTarget(null);
      fetchBlockedEmails();
    } catch (err) {
      showError('Failed', err instanceof Error ? err.message : 'Failed to unblock email');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content mb-2">Email Blocklist</h1>
        <p className="text-base-content/70">
          Manage blocked email addresses. Blocked emails cannot submit new applications.
        </p>
      </div>

      <ErrorBanner error={error} onClose={clearError} />

      {/* Add Form */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-base-content mb-4">Block an Email</h2>
        <form onSubmit={handleAdd} className="flex flex-col md:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            required
            className="flex-1 px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            className="flex-1 px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            type="submit"
            disabled={adding || !email.trim()}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-base-content/40 disabled:cursor-not-allowed font-medium"
          >
            {adding ? 'Blocking...' : 'Block Email'}
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-base-100 shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : blockedEmails.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-base-content/70 text-lg">No blocked emails</p>
            <p className="text-base-content/50 text-sm mt-1">
              Use the form above to block an email address
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-base-300">
            <thead className="bg-base-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Blocked By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {blockedEmails.map((blocked) => (
                <tr key={blocked.id} className="hover:bg-base-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-base-content">
                    {blocked.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    {blocked.reason}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    {blocked.blockedBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    {new Date(blocked.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => setDeleteTarget(blocked)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remove from Blocklist"
        message={
          deleteTarget
            ? `Are you sure you want to unblock "${deleteTarget.email}"? They will be able to submit applications again.`
            : ''
        }
        confirmLabel={deleting ? 'Removing...' : 'Remove'}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmDisabled={deleting}
      />

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default BlocklistManagement;
